/**
 * Preserves patient-record launch context while MarchAgent login completes.
 * This context is not authentication — only navigation/session hints for the workspace.
 */

export const PENDING_SMART_LAUNCH_KEY = 'marchagent:pendingSmartLaunch';
export const SMART_SESSION_STORAGE_KEY = 'march_smart_session_id';

/**
 * Marks "the session id in storage arrived from a real EHR launch on THIS
 * navigation, and the clinician has not acted on it yet".
 *
 * Why this exists: the launch signal used to live only in the URL
 * (`?smart=connected&session_id=...`). Anything that rewrites the URL destroys
 * it. `WorkspaceRedirect` does exactly that — it redirects `/` to the user's
 * default panel with `<Navigate replace>`, which drops the query string — so by
 * the time the entry gate resolved the session, the launch params were gone, it
 * found only the stored id, and it classified a genuine first launch as a
 * same-tab resume. The visible symptom was that the chart-history picker was
 * locked on every route into the app, so the window could never be chosen.
 *
 * Storing the signal separately makes it survive any redirect, and keeps the
 * session id out of the URL (the alternative fix, carrying the query string
 * through, would have left PHI-adjacent identifiers in the address bar).
 *
 * It is one-shot: the gate CONSUMES it when the clinician presses Continue, so
 * a later in-app navigation (Admin and back) correctly resolves as a resume.
 * Consumption is deliberately at Continue rather than at render, so reloading
 * the page before choosing still shows the picker.
 */
export const SMART_LAUNCH_HANDOFF_KEY = 'marchagent:smartLaunchHandoff';

/**
 * When this launch began — the moment the EHR handed the patient over, before
 * sign-in and before the history-window picker (#56).
 *
 * The waiting screen's 90s cap is measured from HERE, not from the moment that
 * screen mounts. Those are different spans: by the time the screen renders the
 * clinician may already have spent a minute signing in and choosing a window,
 * and a cap started then would quietly measure the wrong thing and let a launch
 * run far past the limit it claims to enforce.
 *
 * Stored separately from PENDING_SMART_LAUNCH_KEY (which is cleared as soon as
 * post-login restoration finishes) and alongside SMART_LAUNCH_HANDOFF_KEY, so
 * it survives the sign-in redirect and the picker exactly as the session id
 * does. Cleared when the launch is consumed.
 */
export const SMART_LAUNCH_STARTED_AT_KEY = 'marchagent:smartLaunchStartedAt';

export type PendingSmartLaunch = {
  smart?: string;
  session_id?: string;
  pathname: string;
  search: string;
  capturedAt: number;
};

export type CapturePendingSmartLaunchResult = {
  captured: boolean;
  error?: string;
};

function session(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function getPendingSmartLaunch(): PendingSmartLaunch | null {
  const store = session();
  if (!store) return null;
  const raw = store.getItem(PENDING_SMART_LAUNCH_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingSmartLaunch;
  } catch {
    return null;
  }
}

export function hasPendingSmartLaunch(): boolean {
  return getPendingSmartLaunch() !== null;
}

export function clearPendingSmartLaunch(): void {
  const store = session();
  if (!store) return;
  store.removeItem(PENDING_SMART_LAUNCH_KEY);
}

export function validatePendingSmartLaunch(pending: PendingSmartLaunch | null): string | null {
  if (!pending) return null;
  const smart = (pending.smart ?? '').trim().toLowerCase();
  const sessionId = (pending.session_id ?? '').trim();
  if (smart === 'connected' && !sessionId) {
    return 'The patient record link is incomplete. Please open the patient from the source system again.';
  }
  return null;
}

/** Read SMART query params from the current URL and persist for post-login workspace entry. */
export function capturePendingSmartLaunchFromLocation(): CapturePendingSmartLaunchResult {
  if (typeof window === 'undefined') return { captured: false };

  const url = new URL(window.location.href);
  const smart = (url.searchParams.get('smart') ?? '').trim();
  const sessionId =
    (url.searchParams.get('session_id') ?? '').trim() ||
    (url.searchParams.get('smart_session_id') ?? '').trim();

  if (!smart && !sessionId) {
    return { captured: false };
  }

  const pending: PendingSmartLaunch = {
    smart: smart || undefined,
    session_id: sessionId || undefined,
    pathname: url.pathname,
    search: url.search,
    capturedAt: Date.now(),
  };

  const validationError = validatePendingSmartLaunch(pending);
  if (validationError) {
    return { captured: false, error: validationError };
  }

  const store = session();
  if (store) {
    store.setItem(PENDING_SMART_LAUNCH_KEY, JSON.stringify(pending));
    if (sessionId) {
      store.setItem(SMART_SESSION_STORAGE_KEY, sessionId);
      // Written in the same breath as the session id, because the moment that
      // id lands in storage the URL stops being able to prove where it came
      // from. See SMART_LAUNCH_HANDOFF_KEY.
      store.setItem(SMART_LAUNCH_HANDOFF_KEY, sessionId);
      // #56: stamp the launch start once, here. Re-stamping on a later pass
      // would restart the clock and defeat the cap, so an existing stamp is
      // left alone for this launch.
      if (!store.getItem(SMART_LAUNCH_STARTED_AT_KEY)) {
        store.setItem(SMART_LAUNCH_STARTED_AT_KEY, String(pending.capturedAt));
      }
    }
  }

  return { captured: true };
}

/** The session id handed off by a real EHR launch, if one is still unconsumed. */
export function peekSmartLaunchHandoff(): string | null {
  const store = session();
  if (!store) return null;
  const value = (store.getItem(SMART_LAUNCH_HANDOFF_KEY) ?? '').trim();
  return value || null;
}

/**
 * Spend the launch handoff, so this navigation counts as a launch exactly once.
 *
 * Called when the clinician commits (presses Continue) — not when the picker
 * renders. Consuming at render would mean a page reload before choosing dropped
 * them onto the locked resume screen with no way back to the picker.
 */
export function consumeSmartLaunchHandoff(): void {
  const store = session();
  if (!store) return;
  store.removeItem(SMART_LAUNCH_HANDOFF_KEY);
  // #56: the wait is over once the clinician commits, so the clock stops with
  // the handoff. Leaving it behind would make the NEXT launch in this tab
  // inherit an already-expired start time and trip its cap immediately.
  store.removeItem(SMART_LAUNCH_STARTED_AT_KEY);
}

/**
 * Milliseconds since this launch began, or null when no launch start was
 * recorded (a same-tab resume, or storage unavailable).
 *
 * Null is a real answer, not an error: the caller decides what to do without a
 * measurable span. A malformed or future-dated stamp is treated as unusable
 * rather than trusted, so a corrupt value cannot fabricate a huge elapsed time
 * and trip the cap the instant the screen opens.
 */
export function smartLaunchElapsedMs(now: number = Date.now()): number | null {
  const store = session();
  if (!store) return null;
  const raw = (store.getItem(SMART_LAUNCH_STARTED_AT_KEY) ?? '').trim();
  if (!raw) return null;
  const startedAt = Number.parseInt(raw, 10);
  if (!Number.isFinite(startedAt) || startedAt <= 0 || startedAt > now) return null;
  return now - startedAt;
}

/** Sync pending SMART session id into smartApi session storage. */
export function applyPendingSmartLaunchSession(): void {
  const pending = getPendingSmartLaunch();
  if (!pending?.session_id) return;
  const store = session();
  if (!store) return;
  store.setItem(SMART_SESSION_STORAGE_KEY, pending.session_id.trim());
}

/** Restore pathname/search after login without a full navigation. */
export function restorePendingSmartLaunchUrl(): void {
  const pending = getPendingSmartLaunch();
  if (!pending || typeof window === 'undefined') return;
  const target = `${pending.pathname}${pending.search}`;
  const current = `${window.location.pathname}${window.location.search}`;
  if (target !== current) {
    window.history.replaceState({}, document.title, target);
  }
}

export function completePendingSmartLaunchRestoration(): void {
  applyPendingSmartLaunchSession();
  restorePendingSmartLaunchUrl();
  clearPendingSmartLaunch();
}

/** Preserve current workspace route when session expires so post-login restoration can continue. */
export function captureCurrentWorkspaceRouteForReauth(): void {
  if (typeof window === 'undefined') return;
  if (getPendingSmartLaunch()) return;

  const url = new URL(window.location.href);
  const pathname = url.pathname.trim();
  if (!pathname || pathname === '/' || pathname.includes('/login')) return;

  const pending: PendingSmartLaunch = {
    pathname: url.pathname,
    search: url.search,
    capturedAt: Date.now(),
  };

  const store = session();
  if (store) {
    store.setItem(PENDING_SMART_LAUNCH_KEY, JSON.stringify(pending));
  }
}
